import React, { useState, useEffect } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { D } from '../constants';

const API = '/api';

export default function Login({ onLogin }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const hasVisited = !!localStorage.getItem('eb:visited');

  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const [resetToken, setResetToken] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);
  const [resetError, setResetError] = useState('');
  const [newPin1, setNewPin1] = useState('');
  const [newPin2, setNewPin2] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const [bioBusy, setBioBusy] = useState(false);
  const [bioError, setBioError] = useState('');
  const biometricSupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

  async function handleBiometricLogin() {
    setBioError(''); setBioBusy(true);
    try {
      const optsRes = await fetch(`${API}/webauthn/login-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: company.id, userId: selectedUser.id })
      });
      const opts = await optsRes.json();
      if (!optsRes.ok) throw new Error(opts.error || 'No biometric credential found');

      const assertion = await startAuthentication(opts);

      const verifyRes = await fetch(`${API}/webauthn/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: company.id, userId: selectedUser.id, response: assertion })
      });
      const data = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(data.error || 'Biometric login failed');

      localStorage.setItem('eb:token', data.token);
      localStorage.setItem('eb:visited', '1');
      onLogin(data);
    } catch (e) {
      setBioError(e.name === 'NotAllowedError' ? 'Biometric login was cancelled.' : (e.message || 'Biometric login failed'));
    } finally {
      setBioBusy(false);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('resetToken');
    if (!token) return;
    setResetToken(token);
    setStep('reset');
    fetch(`${API}/auth/reset-pin/${token}`)
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) { setResetError(data.error || 'This reset link is invalid or has expired.'); return; }
        setResetInfo(data);
      })
      .catch(() => setResetError('Could not validate reset link.'));
  }, []);

  async function handleForgotSubmit() {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      setForgotSent(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResetSubmit() {
    setResetError('');
    if (newPin1.length < 4) { setResetError('PIN must be at least 4 digits'); return; }
    if (newPin1 !== newPin2) { setResetError('PINs do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPin: newPin1 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reset PIN');
      setResetDone(true);
    } catch (e) {
      setResetError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailSubmit() {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/company/${encodeURIComponent(email)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Company not found');
      setCompany(data.company);
      setUsers(data.users);
      setStep('user');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePinSubmit() {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: company.id, userId: selectedUser.id, pin })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      localStorage.setItem('eb:token', data.token);
      localStorage.setItem('eb:visited', '1');
      onLogin(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const box = { maxWidth: 360, margin: '120px auto', padding: 32, background: D.card, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.15)' };
  const input = { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${D.border}`, background: D.bg, color: D.text, fontSize: 15, boxSizing: 'border-box', marginBottom: 12 };
  const btn = { width: '100%', padding: '11px', borderRadius: 8, background: D.accent, color: '#fff', border: 'none', fontSize: 15, fontWeight: 600, cursor: 'pointer' };

  return (
    <div style={{ background: D.bg, minHeight: '100vh' }}>
      <div style={box}>
        <h2 style={{ color: D.text, marginBottom: 4, textAlign: 'center' }}>AmpBooks</h2>
        <p style={{ color: D.muted, fontSize: 13, textAlign: 'center', marginBottom: 24 }}>{hasVisited ? 'Welcome back' : 'Welcome to AmpBooks'}</p>

        {step === 'email' && (
          <>
            <p style={{ color: D.muted, marginBottom: 16 }}>Enter your company email to sign in.</p>
            <input style={input} type="email" placeholder="company@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()} />
            {error && <p style={{ color: D.danger, marginBottom: 8 }}>{error}</p>}
            <button style={btn} onClick={handleEmailSubmit} disabled={loading}>{loading ? 'Looking up...' : 'Continue'}</button>
          </>
        )}

        {step === 'user' && (
          <>
            <p style={{ color: D.muted, marginBottom: 16 }}>Select your name:</p>
            {users.map(u => (
              <button key={u.id} onClick={() => { setSelectedUser(u); setStep('pin'); }} style={{ ...btn, background: D.card, color: D.text, border: `1px solid ${D.border}`, marginBottom: 8 }}>{u.name}</button>
            ))}
            <button style={{ ...btn, background: 'transparent', color: D.muted, marginTop: 4 }} onClick={() => { setStep('email'); setError(''); }}>← Back</button>
          </>
        )}

        {step === 'pin' && (
          <>
            <p style={{ color: D.muted, marginBottom: 16 }}>Enter PIN for <strong style={{ color: D.text }}>{selectedUser?.name}</strong>:</p>
            {biometricSupported && selectedUser?.hasBiometric && (
              <>
                <button style={{ ...btn, background: D.success, marginBottom: 12 }} onClick={handleBiometricLogin} disabled={bioBusy}>
                  {bioBusy ? 'Verifying...' : '👆 Use Face ID / Fingerprint'}
                </button>
                {bioError && <p style={{ color: D.danger, marginBottom: 8, fontSize: 13 }}>{bioError}</p>}
                <p style={{ color: D.muted, fontSize: 12, textAlign: 'center', margin: '8px 0' }}>— or enter your PIN —</p>
              </>
            )}
            <input style={input} type="password" placeholder="PIN" value={pin} onChange={e => setPin(e.target.value)} onKeyDown={e => e.key === 'Enter' && handlePinSubmit()} />
            {error && <p style={{ color: D.danger, marginBottom: 8 }}>{error}</p>}
            <button style={btn} onClick={handlePinSubmit} disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
            <button style={{ ...btn, background: 'transparent', color: D.muted, marginTop: 8, fontSize: 13 }} onClick={() => { setStep('user'); setError(''); }}>← Back</button>
            <button style={{ ...btn, background: 'transparent', color: D.accent, marginTop: 4, fontSize: 13 }} onClick={() => { setStep('forgot'); setError(''); setForgotEmail(email); }}>Forgot PIN?</button>
          </>
        )}

        {step === 'forgot' && (
          <>
            {!forgotSent ? (
              <>
                <p style={{ color: D.muted, marginBottom: 16 }}>Enter your company email and we'll send the owner a link to reset their PIN.</p>
                <input style={input} type="email" placeholder="company@email.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleForgotSubmit()} />
                {error && <p style={{ color: D.danger, marginBottom: 8 }}>{error}</p>}
                <button style={btn} onClick={handleForgotSubmit} disabled={loading || !forgotEmail}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
              </>
            ) : (
              <p style={{ color: D.success, marginBottom: 16 }}>If that email is registered, a reset link has been sent. Check your inbox.</p>
            )}
            <button style={{ ...btn, background: 'transparent', color: D.muted, marginTop: 8, fontSize: 13 }} onClick={() => { setStep('email'); setError(''); setForgotSent(false); }}>← Back to sign in</button>
          </>
        )}

        {step === 'reset' && (
          <>
            {resetDone ? (
              <>
                <p style={{ color: D.success, marginBottom: 16 }}>Your PIN has been reset. You can now sign in with your new PIN.</p>
                <button style={btn} onClick={() => { window.location.href = window.location.pathname; }}>Go to Sign In</button>
              </>
            ) : resetError && !resetInfo ? (
              <p style={{ color: D.danger, marginBottom: 16 }}>{resetError}</p>
            ) : !resetInfo ? (
              <p style={{ color: D.muted }}>Validating reset link...</p>
            ) : (
              <>
                <p style={{ color: D.muted, marginBottom: 16 }}>Set a new PIN for <strong style={{ color: D.text }}>{resetInfo.userName}</strong> ({resetInfo.companyName}):</p>
                <input style={input} type="password" placeholder="New PIN" value={newPin1} onChange={e => setNewPin1(e.target.value)} />
                <input style={input} type="password" placeholder="Confirm new PIN" value={newPin2} onChange={e => setNewPin2(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleResetSubmit()} />
                {resetError && <p style={{ color: D.danger, marginBottom: 8 }}>{resetError}</p>}
                <button style={btn} onClick={handleResetSubmit} disabled={loading}>{loading ? 'Saving...' : 'Set New PIN'}</button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
